import React from 'react'
import { FileText, Loader, TrendingDown, TrendingUp } from 'lucide-react'
import {
  getInquiryKey,
  getInquiryTypeLabel,
  getStatusMeta,
} from '@/components/molecules/InquiriesTable'
import { cn, formatCurrency, formatDate } from '@/utils'
import { ORG_LABEL, ORG_SURFACE, OrgEmptyState } from '../OrgPrimitives'
import type { MemberInquiriesBaseProps } from './MemberInquiriesBase'

/** Mobile card list of one member's bids and RFQs, following `InquiriesTable`. */
export const MemberInquiriesMobile: React.FC<MemberInquiriesBaseProps> = ({
  memberLabel,
  items,
  isLoading,
}) => {
  if (isLoading) {
    return (
      <div className="flex items-center justify-center gap-2 py-8">
        <Loader className="animate-spin text-[rgba(45,41,35,0.64)] dark:text-[rgba(253,253,253,0.64)]" size={20} />
        <span className="font-plus-jakarta text-[14px] text-[rgba(45,41,35,0.64)] dark:text-[rgba(253,253,253,0.64)]">
          Loading activity...
        </span>
      </div>
    )
  }

  if (items.length === 0) {
    return (
      <OrgEmptyState
        icon={FileText}
        title="No inquiries yet"
        description={`${memberLabel} has not created any bids or RFQs.`}
        className="py-6"
      />
    )
  }

  return (
    <div className="space-y-3">
      {items.map((inquiry) => {
        const statusMeta = getStatusMeta(inquiry)
        const StatusIcon = statusMeta.icon
        const isBuy = getInquiryTypeLabel(inquiry) === 'Buy'
        const modeLabel = inquiry.inquiryType === 'bid' ? 'Bid' : 'RFQ'

        return (
          <div key={getInquiryKey(inquiry)} className={cn(ORG_SURFACE, 'p-3')}>
            <div className="flex items-center justify-between gap-3">
              <div className="flex min-w-0 items-center gap-3">
                {inquiry.logo ? (
                  <img
                    src={inquiry.logo}
                    alt={inquiry.project_name}
                    className="h-9 w-9 shrink-0 rounded-full object-cover"
                  />
                ) : (
                  <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[rgba(45,41,35,0.12)] font-plus-jakarta text-[11px] font-semibold text-[#2d2923] dark:bg-[rgba(253,253,253,0.16)] dark:text-[#fdfdfd]">
                    {inquiry.project_name.slice(0, 2).toUpperCase()}
                  </div>
                )}
                <span className="truncate font-plus-jakarta text-[14px] font-medium leading-[20px] text-[#2d2923] dark:text-[#fdfdfd]">
                  {inquiry.project_name}
                </span>
              </div>

              <div className="flex shrink-0 items-center gap-2">
                {isBuy ? (
                  <TrendingUp size={16} className="text-[#1fb845]" />
                ) : (
                  <TrendingDown size={16} className="text-[#ff540c]" />
                )}
                <span className="font-plus-jakarta text-[14px] font-normal leading-[20px] text-[#2d2923] dark:text-[#fdfdfd]">
                  {getInquiryTypeLabel(inquiry)}
                </span>
                <span className="inline-flex items-center bg-[rgba(45,41,35,0.12)] px-1 py-[2px] font-plus-jakarta text-[12px] font-medium leading-[16px] text-[#2d2923] dark:bg-[rgba(253,253,253,0.12)] dark:text-[#fdfdfd]">
                  {modeLabel}
                </span>
              </div>
            </div>

            <div className="mt-3 grid grid-cols-2 gap-x-2 gap-y-3">
              <div>
                <p className={ORG_LABEL}>Amount</p>
                <p className="font-plus-jakarta text-[14px] font-normal leading-[20px] text-[#2d2923] dark:text-[#fdfdfd]">
                  {formatCurrency(inquiry.tranche_size)}
                </p>
              </div>
              <div>
                <p className={ORG_LABEL}>Valuation</p>
                <p className="font-plus-jakarta text-[14px] font-normal leading-[20px] text-[#2d2923] dark:text-[#fdfdfd]">
                  {formatCurrency(inquiry.valuation)}
                </p>
              </div>
              <div>
                <p className={ORG_LABEL}>Date</p>
                <p className="font-plus-jakarta text-[14px] font-normal leading-[20px] text-[#2d2923] dark:text-[#fdfdfd]">
                  {formatDate(inquiry.created_at)}
                </p>
              </div>
              <div>
                <p className={ORG_LABEL}>Status</p>
                <span
                  className={cn(
                    'mt-[2px] inline-flex items-center gap-1 border px-[10px] py-[6px] font-plus-jakarta text-[12px] font-medium leading-[16px]',
                    statusMeta.badgeClass,
                  )}
                >
                  <StatusIcon size={12} />
                  {statusMeta.label}
                </span>
              </div>
            </div>
          </div>
        )
      })}
    </div>
  )
}

export default MemberInquiriesMobile
